import React from 'react';
import { ColumnNames } from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/TaxCardMainTable';
import {
    SpecialStatus,
    StatusColumnComponent,
} from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/StatusColomnComponent/StatusColumnComponent';
import useOpenController from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/useOpenController';
import { Button, Icon, Input } from '@tlx/atlas';
import { AdvanceTaxcardDTO, EmployeeDTO } from '@Page/SalaryTaxCard/SalaryTaxCard.types';

import { useIntl } from 'react-intl';
import { getContextId } from '@tlx/astro-shared';
import { TaxcardSelectableMachinery } from '@Page/SalaryTaxCard/hooks/useTaxcardSelectableEmployees';

export interface TaxCardRowProps {
    employee: EmployeeDTO;
    columns: string[];
    skeleton?: boolean;
    lastUpdatedOrderId?: number;
    taxcardSelectEmployeesMachinery: TaxcardSelectableMachinery;
}

interface TaxCardWrapperProps {
    employee: EmployeeDTO;
    columns: string[];
    skeleton?: boolean;
}

export function TaxCardRow({
    employee,
    columns,
    skeleton,
    lastUpdatedOrderId,
    taxcardSelectEmployeesMachinery,
}: TaxCardRowProps) {
    const { isOpen: isExpanded, toggle: toggleExpansion } = useOpenController(false);

    const isFromNewestUpdate =
        employee.taxcard?.orderId == lastUpdatedOrderId &&
        lastUpdatedOrderId != undefined &&
        employee.taxcard?.skattekortIdentifikator != 0;

    return (
        <React.Fragment key={employee.id}>
            {/*<tr onClick={toggle}> I think an alternative to removing it is to prevent the click event from bubling up from the checkbox */}
            <tr
                className={`atl-tr atl-tr--highlight ${isFromNewestUpdate ? 'atl-bg-green-5' : ''}`}
                key={`tr-${employee.id}`}
            >
                {/* this represents one employee/taxcard in the table */}
                <td className="checkbox-row atl-td">
                    <Input
                        type="checkbox"
                        id={employee.id.toString()}
                        value={employee.id}
                        checked={taxcardSelectEmployeesMachinery.isRowChecked(employee.id)}
                        onChange={() => {
                            // If the checkbox is checked we want taxcard for the employee, otherwise not
                            taxcardSelectEmployeesMachinery.onCheckRow(employee.id);
                        }}
                        data-testid={`taxcardrow-employee-${employee.id}-checkbox`}
                    />
                </td>

                <td className="atl-td expand-button">
                    {employee.taxcard && employee.taxcard.advanceTaxcards.length > 1 ? (
                        <RowExpandButton open={isExpanded} toggleRowExpansion={toggleExpansion} />
                    ) : (
                        <></>
                    )}
                </td>
                <TaxCardWrapper employee={employee} columns={columns} skeleton={skeleton} />
            </tr>
            {/* Render the advanceTaxcardDTOs (except for the first, which is already rendered above */}
            {isExpanded &&
                employee.taxcard?.advanceTaxcards.slice(1).map((advanceTaxcard: AdvanceTaxcardDTO, index) => {
                    return (
                        <tr
                            className={isFromNewestUpdate ? 'atl-bg-green-5' : ''}
                            key={`tr-advance-${index}`}
                            onClick={toggleExpansion}
                        >
                            <td className="atl-td atl-whitespace-nowrap" />
                            <td className="atl-td" />
                            <td className="atl-td atl-whitespace-nowrap" />
                            <td className="atl-td atl-whitespace-nowrap" />
                            <TaxCardRowInner advanceTaxcard={advanceTaxcard} columns={columns} skeleton={skeleton} />
                            {columns.includes(ColumnNames.TAXCARD_ADDITIONAL_INFO) ? (
                                <td className="atl-td atl-whitespace-nowrap" />
                            ) : (
                                <></>
                            )}
                            <td className="atl-td atl-whitespace-nowrap" />
                        </tr>
                    );
                })}
        </React.Fragment>
    );
}

function TaxCardWrapper({ employee, columns, skeleton }: TaxCardWrapperProps) {
    const name = `${employee.number ? employee.number + ' ' : ''}${employee.displayName}`;
    {
        return (
            <>
                {columns.includes(ColumnNames.EMPLOYEE_NAME) && (
                    <td className="atl-td">
                        <div className="taxCardMainTable-table__20ch taxcardOverview__tooltip" aria-label={name}>
                            <div className="taxCardMainTable-table-overflow">
                                <span>
                                    <a
                                        className="taxCardMainTable-table__link"
                                        href={
                                            '/execute/employeeMenu?employeeId=' +
                                            employee.id +
                                            '&contextId=' +
                                            getContextId()
                                        }
                                    >
                                        {name}
                                    </a>
                                </span>
                            </div>
                        </div>
                    </td>
                )}
                {columns.includes(ColumnNames.HAS_TAXCARD) && (
                    <td className="atl-td">
                        {StatusColumnComponent(
                            employee.taxcard?.status ?? SpecialStatus.NOT_FETCHED,
                            employee.taxcard?.statusDescription,
                            skeleton,
                        )}
                    </td>
                )}
                <TaxCardRowInner
                    advanceTaxcard={employee.taxcard?.advanceTaxcards[0]}
                    columns={columns}
                    skeleton={skeleton}
                />
                {columns.includes(ColumnNames.TAXCARD_ADDITIONAL_INFO) && (
                    <td className="atl-td">
                        <span className={skeleton ? 'taxCard-skeleton' : ''}>{employee.taxcard?.additionalInfo}</span>
                    </td>
                )}

                {columns.includes(ColumnNames.TAXCARD_LAST_CHANGED) && (
                    <td className="atl-td atl-whitespace-nowrap">
                        <span className={skeleton ? 'taxCard-skeleton' : ''}>{employee.taxcard?.date}</span>
                    </td>
                )}
            </>
        );
    }
}

export interface TaxCardRowInnerProps {
    advanceTaxcard: AdvanceTaxcardDTO | undefined;
    columns: string[];
    skeleton?: boolean;
}

function TaxCardRowInner({ advanceTaxcard, columns, skeleton }: TaxCardRowInnerProps) {
    const intl = useIntl();
    return (
        <>
            {columns.includes(ColumnNames.DEDUCTION_CODE) && (
                <td className="atl-td">
                    <div
                        className="taxCardMainTable-table__20ch taxcardOverview__tooltip"
                        aria-label={
                            advanceTaxcard?.trekkode != ''
                                ? `${intl.formatMessage({
                                      id: 'text_altinn_errors_altinnkode',
                                  })}:\n${advanceTaxcard?.trekkode}`
                                : ''
                        }
                    >
                        <div className="taxCardMainTable-table-overflow">
                            <span className={skeleton ? 'taxCard-skeleton' : ''}>
                                {advanceTaxcard?.trekkodeDescription}
                            </span>
                        </div>
                    </div>
                </td>
            )}

            {columns.includes(ColumnNames.TAXCARD_TYPE) && (
                <td className="atl-td">
                    <div
                        className="taxCardMainTable-table__12ch taxcardOverview__tooltip"
                        aria-label={advanceTaxcard?.typeDescription}
                    >
                        <div className="taxCardMainTable-table-overflow">
                            <span className={skeleton ? 'taxCard-skeleton' : ''}>
                                {advanceTaxcard?.typeDescription}
                            </span>
                        </div>
                    </div>
                </td>
            )}

            {columns.includes(ColumnNames.DEDUCTION_TABLE_NUMBER) && (
                <td className="atl-td atl-whitespace-nowrap atl-text-center">
                    <span className={skeleton ? 'taxCard-skeleton' : ''}>{advanceTaxcard?.tabellnummer}</span>
                </td>
            )}

            {columns.includes(ColumnNames.TAX_PERCENTAGE) && (
                <td className="atl-td atl-whitespace-nowrap atl-text-center">
                    <span className={skeleton ? 'taxCard-skeleton' : ''}>
                        {displayBlankIfZero(advanceTaxcard?.prosentsats)}
                    </span>
                </td>
            )}

            {columns.includes(ColumnNames.TAX_FREE_AMOUNT) && (
                <td className="atl-td atl-whitespace-nowrap atl-text-center">
                    <span className={skeleton ? 'taxCard-skeleton' : ''}>
                        {`${
                            advanceTaxcard?.remainingFreeCardAmount != undefined &&
                            advanceTaxcard?.frikortbelop != undefined &&
                            advanceTaxcard?.frikortbelop > 0 &&
                            advanceTaxcard?.remainingFreeCardAmount != advanceTaxcard?.frikortbelop
                                ? `${advanceTaxcard?.remainingFreeCardAmount} ${intl.formatMessage({
                                      id: 'text_of_lc',
                                  })} `
                                : ''
                        }${displayBlankIfZero(advanceTaxcard?.frikortbelop)}`}
                    </span>
                </td>
            )}
        </>
    );
}

function displayBlankIfZero(number: number | undefined) {
    if (number == undefined || number <= 1) {
        return '';
    }
    return number;
}

type RowExpandButtonProp = {
    id?: number;
    open: boolean;
    toggleRowExpansion: () => void;
};

export function RowExpandButton({ open, toggleRowExpansion }: RowExpandButtonProp) {
    const intl = useIntl();
    return (
        <Button
            variant="icon"
            aria-label={intl.formatMessage({
                id: open ? 'text_collapse' : 'text_expand',
            })}
            aria-expanded={open}
            data-testid="expand-row-button"
            style={{ height: '34px' }}
            onClick={() => {
                toggleRowExpansion();
            }}
        >
            <Icon
                style={{
                    transform: `rotate(${open ? 180 : 0}deg)`,
                    transition: 'all 0.25s',
                }}
            >
                expand_more
            </Icon>
        </Button>
    );
}
